import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  BookOpen, ClipboardList, Download, Eye, FileCheck, FileText, Filter,
  GraduationCap, Loader2, MapPin, Scale, Search, Send, Star,
} from "lucide-react";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../ui/table";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "../ui/dialog";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import DocumentCard from "./DocumentCard";

const API_URL = process.env.REACT_APP_BACKEND_URL;
const FAV_KEY = "biblioteca:favorites";

const CATEGORIE = [
  { key: "normativa", label: "Normative", icon: Scale, color: "bg-blue-50 text-blue-600 ring-blue-100" },
  { key: "modulo", label: "Modelli e Modulistica", icon: FileCheck, color: "bg-emerald-50 text-emerald-600 ring-emerald-100" },
  { key: "guida", label: "Procedure Operative", icon: ClipboardList, color: "bg-orange-50 text-orange-600 ring-orange-100" },
  { key: "pai", label: "Linee Guida", icon: BookOpen, color: "bg-purple-50 text-purple-600 ring-purple-100" },
  { key: "regionale", label: "Aggiornamenti Regionali", icon: MapPin, color: "bg-red-50 text-red-600 ring-red-100" },
  { key: "formazione", label: "Formazione", icon: GraduationCap, color: "bg-teal-50 text-teal-600 ring-teal-100" },
];

const CAT_LABELS = Object.fromEntries(CATEGORIE.map((c) => [c.key, c.label]));
CAT_LABELS.default = "Altri documenti";
CAT_LABELS.pai = "Linee Guida";
CAT_LABELS.guida = "Procedure Operative";
CAT_LABELS.modulo = "Modelli e Modulistica";
CAT_LABELS.normativa = "Normative";

export default function BibliotecaAffidyaTab({ token }) {
  const [moduli, setModuli] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [catFilter, setCatFilter] = useState("all");
  const [favorites, setFavorites] = useState(() => {
    try { return JSON.parse(localStorage.getItem(FAV_KEY) || "[]"); } catch { return []; }
  });
  const [preview, setPreview] = useState(null);
  const [suggestOpen, setSuggestOpen] = useState(false);
  const [suggestText, setSuggestText] = useState("");
  const [sendingSuggest, setSendingSuggest] = useState(false);
  const [showAllEvidenza, setShowAllEvidenza] = useState(false);
  const [showAllRecenti, setShowAllRecenti] = useState(false);

  const fetchModuli = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const { data } = await axios.get(`${API_URL}/api/zoho/modulistica`, { headers: { Authorization: `Bearer ${token}` } });
      setModuli(data.data || []);
    } catch {
      toast.error("Errore caricamento biblioteca");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { fetchModuli(); }, [fetchModuli]);
  useEffect(() => { localStorage.setItem(FAV_KEY, JSON.stringify(favorites)); }, [favorites]);

  const filtered = useMemo(() => {
    const q = search.toLowerCase().trim();
    return moduli.filter((m) => {
      const cat = m.categoria || "default";
      if (catFilter !== "all" && cat !== catFilter) return false;
      if (!q) return true;
      return m.nome.toLowerCase().includes(q) || (m.descrizione || "").toLowerCase().includes(q);
    });
  }, [moduli, search, catFilter]);

  const evidenza = useMemo(() => (showAllEvidenza ? filtered : filtered.slice(0, 4)), [filtered, showAllEvidenza]);
  const recentiOrdinati = useMemo(
    () => [...filtered].sort((a, b) => (b.data_modifica || b.data_creazione || "").localeCompare(a.data_modifica || a.data_creazione || "")),
    [filtered],
  );
  const recenti = useMemo(() => (showAllRecenti ? recentiOrdinati : recentiOrdinati.slice(0, 10)), [recentiOrdinati, showAllRecenti]);

  useEffect(() => { setShowAllEvidenza(false); setShowAllRecenti(false); }, [search, catFilter]);

  const getFileUrl = (m) => {
    if (m.file_id) return `${API_URL}/api/zoho/modulistica/${m.id}/file/${m.file_id}`;
    if (m.file_name) return `/documents/modulistica/${m.file_name}`;
    return null;
  };

  const toggleFav = (id) => setFavorites((p) => p.includes(id) ? p.filter((x) => x !== id) : [...p, id]);

  const formatDate = (m) => {
    const d = m.data_modifica || m.data_creazione;
    return d ? new Date(d).toLocaleDateString("it-IT") : "—";
  };

  const getFileType = (m) => {
    const name = m.file_name || "";
    const ext = name.includes(".") ? name.split(".").pop() : "";
    return ext ? ext.toUpperCase() : "PDF";
  };

  const inviaSuggerimento = async () => {
    if (suggestText.trim().length < 5) { toast.error("Descrivi il suggerimento (minimo 5 caratteri)"); return; }
    setSendingSuggest(true);
    try {
      await axios.post(`${API_URL}/api/biblioteca/suggerimenti`, { testo: suggestText.trim() }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Suggerimento inviato al team Affidya");
      setSuggestText("");
      setSuggestOpen(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore invio suggerimento");
    } finally {
      setSendingSuggest(false);
    }
  };

  return (
    <>
      <div className="mb-5 flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
        <div className="relative flex-1">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <Input placeholder="Cerca in Biblioteca..." value={search} onChange={(e) => setSearch(e.target.value)} className="h-11 rounded-xl pl-10" />
        </div>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" className="h-11 rounded-xl gap-2">
              <Filter className="h-4 w-4" />
              {catFilter === "all" ? "Filtri" : CAT_LABELS[catFilter]}
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            <DropdownMenuItem onClick={() => setCatFilter("all")}>Tutte le categorie</DropdownMenuItem>
            {CATEGORIE.map((c) => (
              <DropdownMenuItem key={c.key} onClick={() => setCatFilter(c.key)}>{c.label}</DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      <div className="mb-8 grid gap-6 lg:grid-cols-[1fr_280px]">
        <div className="space-y-8">
          {/* Banner */}
          <div className="flex items-center justify-between rounded-2xl border bg-gradient-to-r from-purple-50 to-white p-6">
            <div className="flex items-start gap-4">
              <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-purple-100 text-purple-600">
                <BookOpen className="h-7 w-7" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-slate-900">Biblioteca Affidya</h2>
                <p className="mt-1 max-w-xl text-sm text-slate-600">
                  Normative, modelli operativi, procedure e documentazione aggiornata selezionata dal team Affidya.
                </p>
              </div>
            </div>
          </div>

          {/* Categorie */}
          <div>
            <h3 className="mb-3 text-sm font-semibold text-slate-800">Esplora per categoria</h3>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
              {CATEGORIE.map((c) => (
                <button
                  key={c.key}
                  type="button"
                  onClick={() => setCatFilter(catFilter === c.key ? "all" : c.key)}
                  className={`rounded-xl border p-3 text-left transition ${catFilter === c.key ? "border-[#00A86B] bg-emerald-50" : "border-slate-200 bg-white hover:border-slate-300"}`}
                >
                  <div className={`mb-2 flex h-9 w-9 items-center justify-center rounded-lg ring-1 ${c.color}`}>
                    <c.icon className="h-4 w-4" />
                  </div>
                  <p className="text-xs font-semibold text-slate-800">{c.label}</p>
                  <p className="text-[10px] text-slate-400">({moduli.filter((m) => (m.categoria || "default") === c.key).length})</p>
                </button>
              ))}
            </div>
          </div>

          {/* In evidenza */}
          <div>
            <div className="mb-3 flex items-center justify-between">
              <h3 className="text-sm font-semibold text-slate-800">Documenti in evidenza</h3>
              {filtered.length > 4 && (
                <button type="button" className="text-xs font-medium text-[#00A86B] hover:underline" onClick={() => setShowAllEvidenza((v) => !v)}>
                  {showAllEvidenza ? "Mostra meno" : "Vedi tutti"}
                </button>
              )}
            </div>
            {loading ? (
              <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 animate-spin text-[#00A86B]" /></div>
            ) : (
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
                {evidenza.map((m) => (
                  <DocumentCard
                    key={m.id}
                    icon={FileText}
                    iconWrap="bg-violet-50 text-violet-600 ring-violet-100"
                    title={m.nome}
                    description={m.descrizione}
                    badge={<Badge variant="outline" className="text-[10px]">{CAT_LABELS[m.categoria] || m.categoria}</Badge>}
                    meta={formatDate(m)}
                    footerLeft={getFileType(m)}
                    primaryAction={
                      <Button size="sm" variant="ghost" className="h-7 px-2 text-xs" onClick={() => setPreview(m)}>Visualizza</Button>
                    }
                    footerRight={
                      <div className="flex gap-1">
                        <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => toggleFav(m.id)}>
                          <Star className={`h-3.5 w-3.5 ${favorites.includes(m.id) ? "fill-amber-400 text-amber-500" : ""}`} />
                        </Button>
                        <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => window.open(getFileUrl(m), "_blank")}>
                          <Download className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    }
                  />
                ))}
              </div>
            )}
          </div>

          {/* Recenti */}
          <div>
            <div className="mb-3 flex items-center justify-between">
              <h3 className="text-sm font-semibold text-slate-800">Documenti recenti</h3>
              {recentiOrdinati.length > 10 && (
                <button type="button" className="text-xs font-medium text-[#00A86B] hover:underline" onClick={() => setShowAllRecenti((v) => !v)}>
                  {showAllRecenti ? "Mostra meno" : "Vedi tutti"}
                </button>
              )}
            </div>
            <div className="rounded-xl border bg-white">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Titolo</TableHead>
                    <TableHead>Categoria</TableHead>
                    <TableHead>Tipo</TableHead>
                    <TableHead>Data aggiornamento</TableHead>
                    <TableHead className="text-right">Azioni</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {recenti.map((m) => (
                    <TableRow key={m.id}>
                      <TableCell>
                        <p className="font-medium text-slate-800 line-clamp-1">{m.nome}</p>
                        <p className="text-xs text-slate-400 line-clamp-1">{m.descrizione}</p>
                      </TableCell>
                      <TableCell><Badge variant="secondary" className="text-[10px]">{CAT_LABELS[m.categoria] || m.categoria}</Badge></TableCell>
                      <TableCell className="text-xs text-slate-500">{getFileType(m)}</TableCell>
                      <TableCell className="text-xs text-slate-500">{formatDate(m)}</TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1">
                          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => setPreview(m)}><Eye className="h-4 w-4" /></Button>
                          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => window.open(getFileUrl(m), "_blank")}><Download className="h-4 w-4" /></Button>
                          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => toggleFav(m.id)}><Star className={`h-4 w-4 ${favorites.includes(m.id) ? "fill-amber-400 text-amber-500" : ""}`} /></Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>
        </div>

        {/* Sidebar */}
        <aside className="space-y-4">
          <div className="rounded-xl border bg-white p-5">
            <h3 className="font-semibold text-slate-900">La Biblioteca Affidya</h3>
            <p className="mt-2 text-sm text-slate-600">
              Documenti selezionati e aggiornati dal nostro team per offrire supporto concreto e affidabile.
            </p>
            <ul className="mt-4 space-y-2 text-sm text-slate-600">
              <li>• Contenuti sempre aggiornati</li>
              <li>• Selezionati da professionisti del settore</li>
              <li>• Utilizzabili nella gestione quotidiana</li>
              <li>• Disponibili per tutte le strutture</li>
            </ul>
          </div>
          <div className="rounded-xl border bg-slate-50 p-5">
            <h3 className="font-semibold text-slate-900">Hai suggerimenti?</h3>
            <p className="mt-1 text-sm text-slate-500">Proponi un documento da includere nella biblioteca.</p>
            <Button variant="outline" className="mt-3 w-full rounded-xl" onClick={() => setSuggestOpen(true)}>
              Invia suggerimento
            </Button>
          </div>
        </aside>
      </div>

      <Dialog open={!!preview} onOpenChange={() => setPreview(null)}>
        <DialogContent className="max-w-4xl max-h-[90vh]">
          {preview && (
            <>
              <DialogHeader><DialogTitle>{preview.nome}</DialogTitle></DialogHeader>
              <p className="text-sm text-slate-600">{preview.descrizione}</p>
              <div className="rounded-lg bg-slate-100" style={{ height: "60vh" }}>
                <iframe src={getFileUrl(preview)} title={preview.nome} className="h-full w-full" style={{ border: "none" }} />
              </div>
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setPreview(null)}>Chiudi</Button>
                <Button onClick={() => window.open(getFileUrl(preview), "_blank")}><Download className="mr-2 h-4 w-4" />Scarica</Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={suggestOpen} onOpenChange={setSuggestOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Invia un suggerimento</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-slate-500">
            Proponi al team Affidya un documento, una normativa o un aggiornamento da includere nella Biblioteca.
          </p>
          <Textarea
            value={suggestText}
            onChange={(e) => setSuggestText(e.target.value)}
            placeholder="Descrivi il documento o l'aggiornamento che vorresti trovare in Biblioteca…"
            rows={4}
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setSuggestOpen(false)}>Annulla</Button>
            <Button className="bg-[#00A86B] hover:bg-[#00915d]" onClick={inviaSuggerimento} disabled={sendingSuggest}>
              {sendingSuggest ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Send className="mr-2 h-4 w-4" />}
              Invia
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
